import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { DatePickerFr } from './ui/DatePickerFr';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, CheckCircle2, CreditCard, Plus, Receipt, Trash2 } from 'lucide-react';

export default function PlanDialogStep2({ ctx }) {
  const { planForm, setPlanForm, PRICING_TYPES } = ctx;
  return (
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium">Type de tarification *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {PRICING_TYPES.map((type) => {
                      const TypeIcon = type.icon;
                      const isSelected = planForm.type_tarification === type.value;
                      return (
                        <div
                          key={type.value}
                          onClick={() => setPlanForm({ 
                            ...planForm, 
                            type_tarification: type.value,
                            pricing: { ...planForm.pricing, type: type.value }
                          })}
                          className={`
                            p-4 border-2 rounded-xl cursor-pointer transition-all
                            ${isSelected 
                              ? 'border-purple-500 bg-purple-50 shadow-md' 
                              : 'border-gray-200 hover:border-purple-200 hover:bg-gray-50'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-600 text-white' : type.bg}`}>
                              <TypeIcon className={`h-5 w-5 ${isSelected ? '' : type.text}`} />
                            </div>
                            <div className="flex-1">
                              <span className={`font-semibold ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                                {type.label}
                              </span>
                              <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                            </div>
                            {isSelected && (
                              <CheckCircle2 className="h-5 w-5 text-purple-600" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Champs de prix dynamiques */}
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="p-4 space-y-4">
                    <h4 className="font-semibold flex items-center gap-2 text-gray-700">
                      <CreditCard className="h-4 w-4 text-purple-600" />
                      Configuration des prix
                    </h4>
                    
                    {['classique', 'mixte'].includes(planForm.type_tarification) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Prix mensuel (€)</Label>
                          <Input
                            type="number"
                            value={planForm.pricing.prix_mensuel || ''}
                            onChange={(e) => setPlanForm({
                              ...planForm,
                              pricing: { ...planForm.pricing, prix_mensuel: parseFloat(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Prix annuel (€)</Label>
                          <Input
                            type="number"
                            value={planForm.pricing.prix_annuel || ''}
                            onChange={(e) => setPlanForm({
                              ...planForm,
                              pricing: { ...planForm.pricing, prix_annuel: parseFloat(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}

                    {['usage', 'mixte'].includes(planForm.type_tarification) && (
                      <div>
                        <Label className="text-xs text-gray-500">Prix par unité (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={planForm.pricing.prix_unitaire || ''}
                          onChange={(e) => setPlanForm({
                            ...planForm,
                            pricing: { ...planForm.pricing, prix_unitaire: parseFloat(e.target.value) || 0 }
                          })}
                          className="mt-1"
                        />
                      </div>
                    )}

                    {['credits', 'mixte'].includes(planForm.type_tarification) && (
                      <div>
                        <Label className="text-xs text-gray-500">Crédits inclus</Label>
                        <Input
                          type="number"
                          value={planForm.pricing.credits_inclus || ''}
                          onChange={(e) => setPlanForm({
                            ...planForm,
                            pricing: { ...planForm.pricing, credits_inclus: parseInt(e.target.value) || 0 }
                          })}
                          className="mt-1"
                        />
                      </div>
                    )}

                    {planForm.type_tarification === 'wallet' && (
                      <div>
                        <Label className="text-xs text-gray-500">Montant minimum wallet (€)</Label>
                        <Input
                          type="number"
                          value={planForm.pricing.wallet_minimum || ''}
                          onChange={(e) => setPlanForm({
                            ...planForm,
                            pricing: { ...planForm.pricing, wallet_minimum: parseFloat(e.target.value) || 0 }
                          })}
                          className="mt-1"
                        />
                      </div>
                    )}

                    {planForm.type_tarification === 'echelonne' && (() => {
                      const pricing = planForm.pricing || {};
                      const ech = pricing.echeancier || {
                        montant_total: 0,
                        acompte_mode: 'pourcentage',
                        acompte_valeur: 30,
                        nb_echeances: 3,
                        paiement_acompte: 'cb',
                        echeances_dates: [],
                      };
                      const updateEch = (patch) => setPlanForm({
                        ...planForm,
                        pricing: {
                          ...pricing,
                          echeancier: { ...ech, ...patch },
                        },
                      });
                      const acompteEur = ech.acompte_mode === 'montant'
                        ? Number(ech.acompte_valeur || 0)
                        : Math.round(((Number(ech.montant_total || 0) * Number(ech.acompte_valeur || 0)) / 100) * 100) / 100;
                      const resteAPayer = Math.max(0, Number(ech.montant_total || 0) - acompteEur);
                      const montantParEcheance = ech.nb_echeances > 0
                        ? Math.round((resteAPayer / ech.nb_echeances) * 100) / 100
                        : 0;

                      return (
                        <div className="space-y-4 p-3 bg-rose-50 border border-rose-200 rounded-lg" data-testid="echelonne-config">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-rose-600" />
                            <span className="font-semibold text-rose-700 text-sm">Configuration échéancier (sans taux d'intérêt)</span>
                          </div>

                          <div>
                            <Label className="text-xs text-gray-500">Montant total à financer (€) *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={ech.montant_total || ''}
                              onChange={(e) => updateEch({ montant_total: parseFloat(e.target.value) || 0 })}
                              placeholder="Ex: 1200"
                              className="mt-1"
                              data-testid="ech-montant-total"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs text-gray-500">Mode acompte</Label>
                              <Select
                                value={ech.acompte_mode}
                                onValueChange={(v) => updateEch({ acompte_mode: v })}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pourcentage">% du total</SelectItem>
                                  <SelectItem value="montant">Montant fixe €</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">
                                Acompte ({ech.acompte_mode === 'pourcentage' ? '%' : '€'})
                              </Label>
                              <Input
                                type="number"
                                step={ech.acompte_mode === 'pourcentage' ? '1' : '0.01'}
                                value={ech.acompte_valeur || ''}
                                onChange={(e) => updateEch({ acompte_valeur: parseFloat(e.target.value) || 0 })}
                                className="mt-1"
                                data-testid="ech-acompte-valeur"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Nb d'échéances</Label>
                              <Input
                                type="number"
                                min="1"
                                max="36"
                                value={ech.nb_echeances || ''}
                                onChange={(e) => updateEch({ nb_echeances: parseInt(e.target.value) || 0 })}
                                className="mt-1"
                                data-testid="ech-nb-echeances"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs text-gray-500">Paiement de l'acompte</Label>
                            <Select
                              value={ech.paiement_acompte}
                              onValueChange={(v) => updateEch({ paiement_acompte: v })}
                            >
                              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cb">💳 Carte bancaire (GoCardless)</SelectItem>
                                <SelectItem value="virement">🏦 Virement bancaire (OBJECTIF SCOP OUTREMER)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label className="text-xs text-gray-500">Dates d'échéances (optionnel — auto-mensuel si vide)</Label>
                            <div className="space-y-1 mt-1">
                              {Array.from({ length: ech.nb_echeances || 0 }).map((_, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-500 w-24">Échéance {i + 1}</span>
                                  <DatePickerFr
                                    value={ech.echeances_dates?.[i] || ''}
                                    onChange={(v) => {
                                      const dates = [...(ech.echeances_dates || [])];
                                      dates[i] = v;
                                      updateEch({ echeances_dates: dates });
                                    }}
                                    className="h-8 text-xs"
                                    data-testid={`ech-date-${i}`}
                                  />
                                  <span className="text-gray-600 font-semibold whitespace-nowrap">
                                    {montantParEcheance.toFixed(2)} €
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="text-xs bg-white rounded p-2 border border-rose-200 space-y-0.5">
                            <div className="flex justify-between"><span className="text-gray-600">Montant total :</span><span className="font-semibold">{Number(ech.montant_total || 0).toFixed(2)} €</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Acompte :</span><span className="font-semibold text-rose-700">{acompteEur.toFixed(2)} €</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Reste à financer :</span><span className="font-semibold">{resteAPayer.toFixed(2)} €</span></div>
                            <div className="flex justify-between border-t pt-0.5 mt-0.5"><span className="text-gray-600">× {ech.nb_echeances} échéances :</span><span className="font-bold text-rose-700">{montantParEcheance.toFixed(2)} € / mois</span></div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* === Taxes personnalisées (TVA, octroi de mer, contributions…) === */}
                    <div className="pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                            <Receipt className="w-4 h-4 text-indigo-600" />
                            Taxes personnalisées
                          </Label>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Affichées exclusivement sur la facture/reçu (Montant HT + taxes = TTC). En front, seul le TTC est exposé.
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPlanForm({
                            ...planForm,
                            pricing: {
                              ...planForm.pricing,
                              taxes_personnalisees: [
                                ...(planForm.pricing.taxes_personnalisees || []),
                                { libelle: '', type: 'pourcentage', valeur: 0 }
                              ]
                            }
                          })}
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          data-testid="plan-add-tax-btn"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Ajouter une taxe
                        </Button>
                      </div>
                      {(planForm.pricing.taxes_personnalisees || []).length === 0 && (
                        <div className="text-xs text-slate-400 italic px-3 py-2 bg-slate-50 rounded">
                          Aucune taxe personnalisée. Le TTC = HT (ou TVA standard utilisée).
                        </div>
                      )}
                      <div className="space-y-2">
                        {(planForm.pricing.taxes_personnalisees || []).map((tax, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-indigo-50/40 border border-indigo-100 rounded-lg" data-testid={`plan-tax-row-${idx}`}>
                            <Input
                              placeholder="Libellé (ex: TVA, Octroi de mer…)"
                              value={tax.libelle}
                              onChange={(e) => {
                                const next = [...planForm.pricing.taxes_personnalisees];
                                next[idx] = { ...next[idx], libelle: e.target.value };
                                setPlanForm({ ...planForm, pricing: { ...planForm.pricing, taxes_personnalisees: next } });
                              }}
                              className="flex-1"
                              data-testid={`plan-tax-libelle-${idx}`}
                            />
                            <Select
                              value={tax.type}
                              onValueChange={(v) => {
                                const next = [...planForm.pricing.taxes_personnalisees];
                                next[idx] = { ...next[idx], type: v };
                                setPlanForm({ ...planForm, pricing: { ...planForm.pricing, taxes_personnalisees: next } });
                              }}
                            >
                              <SelectTrigger className="w-32" data-testid={`plan-tax-type-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pourcentage">Pourcentage</SelectItem>
                                <SelectItem value="montant">Montant fixe</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="relative w-28">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0"
                                value={tax.valeur}
                                onChange={(e) => {
                                  const next = [...planForm.pricing.taxes_personnalisees];
                                  next[idx] = { ...next[idx], valeur: parseFloat(e.target.value) || 0 };
                                  setPlanForm({ ...planForm, pricing: { ...planForm.pricing, taxes_personnalisees: next } });
                                }}
                                className="pr-7"
                                data-testid={`plan-tax-valeur-${idx}`}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                                {tax.type === 'pourcentage' ? '%' : '€'}
                              </span>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const next = planForm.pricing.taxes_personnalisees.filter((_, i) => i !== idx);
                                setPlanForm({ ...planForm, pricing: { ...planForm.pricing, taxes_personnalisees: next } });
                              }}
                              className="text-red-600 hover:bg-red-50 px-2"
                              data-testid={`plan-tax-remove-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
  );
}
